import React from 'react';
import { Maximize, Zap, Sparkles, Layers, Sliders } from 'lucide-react';
import { ImpositionLayout, VdpTemplate } from '../../types';

interface PressPresetsBarProps {
  layout: ImpositionLayout;
  template: VdpTemplate;
  onUpdateLayout: (key: string, value: any) => void;
  onAutoOptimizeGrid?: () => void;
}

export const PressPresetsBar: React.FC<PressPresetsBarProps> = ({
  layout,
  template,
  onUpdateLayout,
  onAutoOptimizeGrid
}) => {

  const presets = [
    { name: '12"×18" Digital Press', widthMm: 304.8, heightMm: 457.2, code: '12x18' },
    { name: '13"×19" Super A3/SRA3', widthMm: 330, heightMm: 483, code: '13x19' },
    { name: '18"×24" Medium Press', widthMm: 457.2, heightMm: 609.6, code: '18x24' },
    { name: '24" Vinyl Roll', widthMm: 609.6, heightMm: 1000, code: '24roll' },
    { name: '36" Vinyl Roll', widthMm: 914.4, heightMm: 1000, code: '36roll' },
    { name: 'A3 (297×420mm)', widthMm: 297, heightMm: 420, code: 'A3' },
    { name: 'A4 (210×297mm)', widthMm: 210, heightMm: 297, code: 'A4' },
  ];

  const handleApplyPreset = (preset: typeof presets[0]) => {
    // Convert mm to pt (1 mm = 2.83465 pt)
    const wPt = preset.widthMm * 2.83465;
    const hPt = preset.heightMm * 2.83465;

    onUpdateLayout('pageSize', preset.code);
    onUpdateLayout('pageWidth', wPt);
    onUpdateLayout('pageHeight', hPt);

    // Auto calculate optimal rows & columns for this sheet size
    const bleedPt = layout.bleed ?? 0;
    const itemWPt = (template.width || 300) + (bleedPt * 2);
    const itemHPt = (template.height || 300) + (bleedPt * 2);
    const marginTop = layout.marginTop ?? 14.17;
    const marginLeft = layout.marginLeft ?? 14.17;
    const marginRight = layout.marginRight ?? 14.17;
    const marginBottom = layout.marginBottom ?? 14.17;

    const availW = wPt - (marginLeft + marginRight);
    const availH = hPt - (marginTop + marginBottom);

    const cols = Math.max(1, Math.floor(availW / itemWPt));
    const rows = Math.max(1, Math.floor(availH / itemHPt));

    onUpdateLayout('columns', cols);
    onUpdateLayout('rows', rows);
  };

  return (
    <div className="bg-zinc-950 border border-zinc-800 p-3 rounded-xl space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Maximize className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-bold text-zinc-100">1-Click Press Sheet Presets</span>
        </div>
        {onAutoOptimizeGrid && (
          <button
            onClick={onAutoOptimizeGrid}
            className="px-2.5 py-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
          >
            <Zap className="w-3.5 h-3.5" /> Maximize N-Up Density
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {presets.map((p) => {
          const isSelected = layout.pageSize === p.code || (Math.abs((layout.pageWidth || 0) - (p.widthMm * 2.83465)) < 5);
          return (
            <button
              key={p.code}
              onClick={() => handleApplyPreset(p)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all border cursor-pointer ${
                isSelected 
                  ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-900/30 font-bold' 
                  : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800 hover:border-zinc-700'
              }`}
            >
              {p.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};
